import React from 'react';
import { buttonVariants, radii, transitions } from '../../theme';

type Variant = keyof typeof buttonVariants;
type Size = 'sm' | 'md' | 'lg';

const sizeClasses: Record<Size, string> = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-2.5 text-base',
};

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'neutral',
    size = 'md',
    loading = false,
    className = '',
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const tone = buttonVariants[variant] ?? buttonVariants.neutral;
  const sizeClass = sizeClasses[size];
  const isDisabled = disabled || loading;
  const spinnerTone = variant === 'neutral' || variant === 'ghost'
    ? 'border-border-primary border-t-nightshade-500'
    : 'border-white/60 border-t-white';

  return (
    <button
      ref={ref}
      type={type}
      className={`inline-flex items-center justify-center gap-2 ${radii.sm} ${sizeClass} font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${transitions.default} ${tone} ${className}`}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          className={`h-4 w-4 animate-spin rounded-full border-2 ${spinnerTone}`}
          aria-hidden
        />
      )}
      {children}
    </button>
  );
});
